<template>
  <div class="user-container">
    <el-card shadow="never">
      <!-- 工具栏 -->
      <div class="toolbar">
        <el-input
          v-model.trim="keyword"
          placeholder="搜索用户名 / 邮箱"
          clearable
          prefix-icon="el-icon-search"
          style="width: 240px"
          @keyup.enter.native="handleSearch"
          @clear="handleSearch"
        />
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
        <el-button type="success" icon="el-icon-plus" @click="handleAdd">新增用户</el-button>
        <el-button icon="el-icon-refresh" @click="fetchPermTree">刷新权限树</el-button>
      </div>

      <!-- 表格 -->
      <el-table
        v-loading="loading"
        :data="tableData"
        border
        stripe
        style="width: 100%"
      >
        <el-table-column type="index" label="#" width="50" align="center" />
        <el-table-column prop="username" label="用户名" min-width="120" />
        <el-table-column prop="role" label="角色" width="140" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.role" :type="isSuperRole(row.role) ? 'danger' : 'info'">
              {{ row.role }}
            </el-tag>
            <span v-else class="perm-empty">未分配</span>
          </template>
        </el-table-column>
        <el-table-column prop="email" label="邮箱" min-width="150">
          <template slot-scope="{ row }">
            <span v-if="row.email">{{ row.email }}</span>
            <span v-else class="perm-empty">-</span>
          </template>
        </el-table-column>
        <!-- 特殊权限 special -->
        <el-table-column label="特殊权限" min-width="150">
          <template slot-scope="{ row }">
            <span v-if="!row.special || !row.special.length" class="perm-empty">
              无
            </span>
            <el-tooltip v-else placement="top" :disabled="row.special.length <= 2">
              <div slot="content" class="perm-tip-box">
                <div v-for="s in row.special" :key="s">{{ s }}</div>
              </div>
              <div class="perm-tags">
                <el-tag
                  v-for="s in row.special.slice(0, 2)"
                  :key="s"
                  size="mini"
                  :type="specialType(s)"
                >
                  {{ s }}
                </el-tag>
                <el-tag v-if="row.special.length > 2" size="mini">
                  +{{ row.special.length - 2 }}
                </el-tag>
              </div>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- 应用权限 -->
        <el-table-column label="应用权限" min-width="220">
          <template slot-scope="{ row }">
            <el-tag v-if="isSuperRole(row.role)" type="danger" size="mini">
              全部权限
            </el-tag>
            <span v-else-if="!row.perms || !row.perms.length" class="perm-empty">
              未配置
            </span>
            <el-tooltip v-else placement="top" :disabled="row.perms.length <= 2">
              <div slot="content" class="perm-tip-box">
                <div v-for="p in row.perms" :key="p">{{ formatPerm(p) }}</div>
              </div>
              <div class="perm-tags">
                <el-tag
                  v-for="p in row.perms.slice(0, 2)"
                  :key="p"
                  size="mini"
                  :type="permType(p)"
                >
                  {{ formatPerm(p) }}
                </el-tag>
                <el-tag v-if="row.perms.length > 2" size="mini">
                  +{{ row.perms.length - 2 }}
                </el-tag>
              </div>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column prop="createdAt" label="创建时间" min-width="160">
          <template slot-scope="{ row }">
            {{ row.createdAt ? row.createdAt.replace('T', ' ').slice(0, 19) : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" icon="el-icon-edit" @click="handleEdit(row)">
              编辑
            </el-button>
            <el-button
              type="text"
              size="small"
              class="delete-btn"
              icon="el-icon-delete"
              @click="handleDelete(row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          :current-page.sync="page"
          :page-size="size"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          background
          @size-change="handleSizeChange"
          @current-change="fetchData"
        />
      </div>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="720px"
      :close-on-click-modal="false"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="80px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model.trim="form.username" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input
            v-model.trim="form.password"
            type="password"
            show-password
            :placeholder="isEdit ? '留空则不修改密码' : '请输入密码'"
          />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input
            v-model.trim="form.email"
            placeholder="选填，用于通知与找回账号"
            clearable
          />
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-select
            v-model="form.role"
            filterable
            clearable
            :loading="roleLoading"
            style="width: 100%"
            :placeholder="roleOptions.length ? '请选择角色' : '暂无角色，请先到角色管理创建'"
          >
            <el-option
              v-for="r in roleOptions"
              :key="r._id"
              :label="r.roleName"
              :value="r.roleName"
            >
              <span>{{ r.roleName }}</span>
              <span v-if="r.remark" class="role-option-remark">{{ r.remark }}</span>
            </el-option>
          </el-select>
          <div v-if="isSuperRole(form.role)" class="perm-tip">
            超级管理员默认拥有全部应用权限，下方配置不会对其生效（但仍会保存）。
          </div>
          <div v-if="!roleOptions.length" class="perm-tip perm-tip-warn">
            角色列表为空，请先在「角色管理」中创建角色。
          </div>
        </el-form-item>
        <el-form-item label="特殊权限" prop="special">
          <el-select
            v-model="form.special"
            multiple
            filterable
            clearable
            collapse-tags
            style="width: 100%"
            placeholder="可多选功能标识"
          >
            <el-option v-for="s in specialOptions" :key="s" :label="s" :value="s" />
          </el-select>
          <div class="perm-tip">
            功能开关，与「角色」「应用权限」相互独立：角色与权限控制能看哪些应用，special 控制能用哪些功能。
          </div>
        </el-form-item>

        <el-form-item label="应用权限">
          <div class="perm-search">
            <el-input
              v-model.trim="permFilter"
              placeholder="搜索项目 / 地域 / 应用"
              clearable
              prefix-icon="el-icon-search"
            />
            <el-button size="small" @click="checkAll">全选</el-button>
            <el-button size="small" @click="clearChecked">清空</el-button>
          </div>

          <div class="perm-tree-box">
            <el-tree
              ref="permTree"
              :data="permTree"
              :props="treeProps"
              node-key="path"
              show-checkbox
              check-strictly
              default-expand-all
              :filter-node-method="filterNode"
              @check-change="onCheckChange"
            >
              <span slot-scope="{ node, data }" class="perm-node">
                <el-tag :type="permType(data.path)" size="mini">
                  {{ levelText(data.level) }}
                </el-tag>
                <span class="perm-node-label">{{ node.label }}</span>
                <span v-if="coveredCount(data.path)" class="perm-node-cover">
                  含其下 {{ coveredCount(data.path) }} 项
                </span>
              </span>
            </el-tree>
          </div>

          <div class="perm-tip">
            勾选<b>项目</b>即拥有该项目下所有地域与应用；勾选<b>地域</b>即拥有该地域下所有应用；也可只勾选<b>单个应用</b>。可多选、互不冲突。
            <br />
            勾选某节点时会自动移除其<b>上级</b>与<b>下级</b>勾选（二者互相覆盖），无需手动清理。
          </div>

          <div v-if="selectedPerms.length" class="perm-selected">
            <div class="perm-selected-title">
              已选 {{ selectedPerms.length }} 项：
            </div>
            <el-tag
              v-for="p in selectedPerms"
              :key="p"
              size="mini"
              :type="permType(p)"
              closable
              @close="removePerm(p)"
            >
              {{ formatPerm(p) }}
            </el-tag>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">
          确 定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getUserList, createUser, updateUser, deleteUser } from '../api/user';
import { getAppTree } from '../api/app';
import { getRoleAll } from '../api/role';

// 超级管理员角色名：走「全部权限」直通，需与后端保持一致
const SUPER_ROLE = 'admin';

export default {
  name: 'User',
  data() {
    return {
      loading: false,
      tableData: [],
      keyword: '',
      page: 1,
      size: 10,
      total: 0,
      // 权限树（项目 -> 地域 -> 应用），每个节点带完整 path
      permTree: [],
      permFilter: '',
      // 树当前勾选的 key（完整权限路径），与计算属性解耦避免互相触发
      rawCheckedKeys: [],
      // 角色列表（来自角色管理模块）
      roleOptions: [],
      roleLoading: false,
      // 弹窗
      dialogVisible: false,
      submitting: false,
      isEdit: false,
      // special 可选值白名单，需与后端 models/User.js 的 SPECIAL_VALUES 保持一致
      specialOptions: [
        'debug',
        'vsServerList',
        'vsServerListState',
        'deductItem',
        'timeOpenServer',
        'notDisplayBan',
        'giftkey',
        'admin'
      ],
      form: {
        id: '',
        username: '',
        password: '',
        email: '',
        role: '',
        special: [],
        perms: []
      },
      rules: {
        username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
        role: [{ required: true, message: '请选择角色', trigger: 'change' }],
        email: [
          {
            validator: (rule, value, callback) => {
              if (!value) return callback(); // 选填
              if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                return callback(new Error('邮箱格式不正确'));
              }
              callback();
            },
            trigger: 'blur'
          }
        ],
        password: [
          {
            validator: (rule, value, callback) => {
              if (!this.isEdit && !value) {
                return callback(new Error('请输入密码'));
              }
              callback();
            },
            trigger: 'blur'
          }
        ]
      }
    };
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑用户' : '新增用户';
    },
    treeProps() {
      return { children: 'children', label: 'label' };
    },
    // 树中出现过的全部有效权限路径（回显时用来过滤失效项）
    validPaths() {
      const set = new Set();
      const walk = nodes => {
        (nodes || []).forEach(n => {
          set.add(n.path);
          if (n.children && n.children.length) walk(n.children);
        });
      };
      walk(this.permTree);
      return set;
    },
    // 当前勾选、归一化后的权限列表
    selectedPerms() {
      return this.normalizePerms(this.rawCheckedKeys);
    }
  },
  watch: {
    // 树内搜索
    permFilter(val) {
      this.$refs.permTree && this.$refs.permTree.filter(val);
    }
  },
  created() {
    this.fetchRoles();
    this.fetchPermTree();
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await getUserList({
          page: this.page,
          size: this.size,
          keyword: this.keyword
        });
        this.tableData = data.data.list;
        this.total = data.data.total;
      } catch (err) {
        this.$message.error(err.message);
      } finally {
        this.loading = false;
      }
    },
    async fetchPermTree() {
      try {
        const { data } = await getAppTree();
        this.permTree = this.buildTree(data.data || []);
      } catch (err) {
        this.$message.error(err.message || '权限树加载失败');
      }
    },
    // 角色列表：来自角色管理模块（GET /api/role/all）
    async fetchRoles() {
      this.roleLoading = true;
      try {
        const { data } = await getRoleAll();
        this.roleOptions = data.data || [];
      } catch (err) {
        this.$message.error(err.message || '角色列表加载失败');
      } finally {
        this.roleLoading = false;
      }
    },
    // 是否超级管理员角色
    isSuperRole(role) {
      return role === SUPER_ROLE;
    },

    // 后端返回的三级树 -> 补上完整 path 字段
    buildTree(nodes, prefix) {
      return (nodes || []).map(n => {
        const path = prefix ? `${prefix}/${n.value}` : String(n.value);
        return {
          path,
          label: n.label,
          level: n.level,
          children: n.children && n.children.length
            ? this.buildTree(n.children, path)
            : []
        };
      });
    },

    handleSearch() {
      this.page = 1;
      this.fetchData();
    },
    handleSizeChange(size) {
      this.size = size;
      this.page = 1;
      this.fetchData();
    },

    /* ---------- 权限相关 ---------- */
    // 去重去空，并移除已被父级覆盖的冗余子项
    normalizePerms(perms) {
      const list = Array.from(new Set((perms || []).filter(Boolean)));
      return list.filter(p => !list.some(o => o !== p && p.startsWith(o + '/')));
    },
    // 取某个节点的全部祖先路径
    ancestorsOf(path) {
      const parts = String(path).split('/');
      return parts.slice(0, -1).map((_, i) => parts.slice(0, i + 1).join('/'));
    },
    /**
     * 勾选变化：勾中某节点时，自动移除其祖先与后代
     * 因为父级覆盖子级，二者同时存在没有意义，且会让子级被 normalize 静默吞掉
     */
    onCheckChange(data, checked) {
      if (this._syncing) return;
      const tree = this.$refs.permTree;
      if (!tree) return;

      if (!checked) {
        this.rawCheckedKeys = tree.getCheckedKeys() || [];
        return;
      }

      const path = data.path;
      const ancestors = this.ancestorsOf(path);
      const keys = (tree.getCheckedKeys() || []).filter(k => {
        if (k === path) return true;
        if (ancestors.indexOf(k) !== -1) return false;        // 去掉祖先
        if (k.indexOf(path + '/') === 0) return false;        // 去掉后代
        return true;
      });

      this.rawCheckedKeys = keys;
      this.applyChecked(keys);
    },
    // 统一写回树，加 guard 防止 setCheckedKeys 触发 check-change 造成递归
    applyChecked(keys) {
      this._syncing = true;
      this.$nextTick(() => {
        if (this.$refs.permTree) this.$refs.permTree.setCheckedKeys(keys, false);
        this.$nextTick(() => { this._syncing = false; });
      });
    },
    // 节点下包含多少后代（用于「含其下 N 项」提示）
    coveredCount(path) {
      const node = this.findNode(this.permTree, path);
      if (!node) return 0;
      let n = 0;
      const walk = list => {
        (list || []).forEach(c => { n++; walk(c.children); });
      };
      walk(node.children);
      return n;
    },
    findNode(nodes, path) {
      for (const n of nodes || []) {
        if (n.path === path) return n;
        const hit = this.findNode(n.children, path);
        if (hit) return hit;
      }
      return null;
    },
    // 回显：过滤掉树里已不存在的失效项
    setCheckedPerms(perms) {
      const keys = this.normalizePerms(perms).filter(p => this.validPaths.has(p));
      this.rawCheckedKeys = keys;
      this.applyChecked(keys);
    },
    checkAll() {
      const all = [];
      const walk = nodes => {
        (nodes || []).forEach(n => {
          all.push(n.path);
          if (n.children && n.children.length) walk(n.children);
        });
      };
      walk(this.permTree);
      // 全选后只保留顶层项目，等价于全权限且更简洁
      const roots = all.filter(p => p.split('/').length === 1);
      this.rawCheckedKeys = roots.length ? roots : all;
      this.applyChecked(this.rawCheckedKeys);
    },
    clearChecked() {
      this.rawCheckedKeys = [];
      this.applyChecked([]);
    },
    removePerm(p) {
      this.rawCheckedKeys = this.rawCheckedKeys.filter(k => k !== p);
      this.applyChecked(this.rawCheckedKeys);
    },
    filterNode(value, data) {
      if (!value) return true;
      return String(data.path).toLowerCase().indexOf(String(value).toLowerCase()) !== -1
        || String(data.label).toLowerCase().indexOf(String(value).toLowerCase()) !== -1;
    },
    formatPerm(p) {
      const parts = String(p || '').split('/');
      if (parts.length === 1) return `项目 ${parts[0]}`;
      if (parts.length === 2) return `${parts[0]} / ${parts[1]}`;
      // 应用级带上完整前缀，避免同名 app_id 混淆
      return `${parts[0]} / ${parts[1]} / ${parts[2]}`;
    },
    permType(p) {
      const level = String(p || '').split('/').length;
      return level === 1 ? 'warning' : level === 2 ? 'success' : 'info';
    },
    levelText(level) {
      return level === 'project' ? '项目' : level === 'region' ? '地域' : '应用';
    },
    // special 展示配色：admin 高亮，其余按名字稳定性取色
    specialType(s) {
      if (s === 'admin') return 'danger';
      if (s === 'debug') return 'warning';
      return 'info';
    },

    /* ---------- 弹窗 ---------- */
    handleAdd() {
      this.isEdit = false;
      this.form = {
        id: '',
        username: '',
        password: '',
        email: '',
        // 默认取第一个角色，避免必填项空白
        role: this.roleOptions.length ? this.roleOptions[0].roleName : '',
        special: [],
        perms: []
      };
      this.permFilter = '';
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.form.clearValidate();
        this.setCheckedPerms([]);
      });
    },
    handleEdit(row) {
      this.isEdit = true;
      // 角色已被删除时给出提示，避免静默回显成空
      const roleMissing = row.role
        && this.roleOptions.length
        && !this.roleOptions.some(r => r.roleName === row.role);
      if (roleMissing) {
        this.$message.warning(`该用户的角色「${row.role}」已不存在，请重新选择`);
      }
      this.form = {
        id: row._id,
        username: row.username,
        password: '',
        email: row.email || '',
        role: row.role || '',
        // 过滤掉后端已移除的旧值，避免 el-select 出现空白 tag
        special: (row.special || []).filter(s => this.specialOptions.indexOf(s) !== -1),
        perms: row.perms || []
      };
      this.permFilter = '';
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.form.clearValidate();
        this.setCheckedPerms(row.perms);
      });
    },
    async handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return;
        this.submitting = true;
        try {
          const payload = Object.assign({}, this.form, {
            perms: this.selectedPerms
          });
          if (this.isEdit) {
            await updateUser(payload);
          } else {
            await createUser(payload);
          }
          this.$message.success(this.isEdit ? '更新成功' : '创建成功');
          this.dialogVisible = false;
          this.fetchData();
        } catch (err) {
          this.$message.error(err.message);
        } finally {
          this.submitting = false;
        }
      });
    },
    handleDelete(row) {
      this.$confirm(`确定删除用户 "${row.username}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteUser(row._id);
          this.$message.success('删除成功');
          // 删除后如果当前页空了，退回上一页
          if (this.tableData.length === 1 && this.page > 1) {
            this.page--;
          }
          this.fetchData();
        } catch (err) {
          this.$message.error(err.message);
        }
      }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.user-container {
  padding: 10px;
}
.toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}
.delete-btn {
  color: #F56C6C;
}
.pagination {
  margin-top: 20px;
  text-align: right;
}

/* 权限列 */
.perm-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.perm-empty {
  color: #c0c4cc;
}
.perm-tip-box {
  max-height: 260px;
  overflow: auto;
  line-height: 1.9;
}

/* 权限树 */
.perm-search {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
.perm-tree-box {
  width: 100%;
  max-height: 300px;
  padding: 8px;
  overflow: auto;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}
.perm-node {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}
/* 角色下拉 */
.role-option-remark {
  float: right;
  margin-left: 16px;
  font-size: 12px;
  color: #c0c4cc;
}
.perm-tip-warn {
  color: #e6a23c;
}

.perm-node-label {
  color: #606266;
}
.perm-node-cover {
  margin-left: 2px;
  font-size: 12px;
  color: #c0c4cc;
}

/* 弹窗内提示 */
.perm-tip {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.6;
  color: #909399;
}
.perm-tip b {
  color: #606266;
}
.perm-selected {
  margin-top: 8px;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 4px;
}
.perm-selected-title {
  margin-bottom: 6px;
  font-size: 12px;
  color: #606266;
}
.perm-selected .el-tag {
  margin: 0 4px 4px 0;
}
</style>
